import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinks = [
  { label: "nav.spreadsheet", path: "/cssbuy-spreadsheet/" },
  { label: "nav.review", path: "/cssbuy-review/" },
  { label: "nav.shipping", path: "/cssbuy-shipping/" },
  { label: "nav.safety", path: "/cssbuy-safety/" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language;
  const langMap: Record<string, string> = {
    en: "English",
    "pt-BR": "Português",
    de: "Deutsch",
  };

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const switchLang = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b-2 border-primary-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
              CSSBuy
            </span>
            <span className="text-xs md:text-sm font-semibold text-primary-700 bg-primary-50 px-2 py-0.5 rounded-md">
              Spreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 lg:gap-1.5">
            <Link
              to="/"
              className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              {t("nav.home")}
            </Link>

            <Link
              to="/cssbuy-beginners/"
              className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-guide-line" />
              {t("nav.shoppingGuide")}
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                {t("dropdown.label")}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-background-200 rounded-xl py-2 z-50 max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-foreground-800 hover:text-primary-700 hover:bg-primary-50 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-6 h-6 flex items-center justify-center text-primary-600">
                        <i className={cat.icon} />
                      </span>
                      {t(cat.key)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                {t(link.label)}
              </Link>
            ))}
          </nav>

          {/* Right side: Language + CTA */}
          <div className="hidden md:flex items-center gap-2">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <i className="ri-global-line" />
                <span className="hidden lg:inline">{langMap[currentLang] || "English"}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-36 bg-white border border-background-200 rounded-xl py-2 z-50">
                  <button onClick={() => switchLang("en")} className="w-full text-left px-4 py-2 text-sm font-medium text-foreground-800 hover:text-primary-700 hover:bg-primary-50 transition-colors">
                    {t("lang.en")}
                  </button>
                  <button onClick={() => switchLang("pt-BR")} className="w-full text-left px-4 py-2 text-sm font-medium text-foreground-800 hover:text-primary-700 hover:bg-primary-50 transition-colors">
                    {t("lang.pt")}
                  </button>
                  <button onClick={() => switchLang("de")} className="w-full text-left px-4 py-2 text-sm font-medium text-foreground-800 hover:text-primary-700 hover:bg-primary-50 transition-colors">
                    {t("lang.de")}
                  </button>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t("nav.cta")}
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden p-2 text-foreground-900 hover:text-primary-700 rounded-lg"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-white px-4 py-5 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-1.5 px-3 py-3 text-sm font-semibold text-white bg-primary-500 rounded-lg"
            >
              <i className="ri-home-4-line" />
              {t("nav.home")}
            </Link>
            <Link
              to="/cssbuy-beginners/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-1.5 px-3 py-3 text-sm font-semibold text-primary-700 bg-primary-50 rounded-lg"
            >
              <i className="ri-guide-line" />
              {t("nav.shoppingGuide")}
            </Link>
          </div>

          {/* Categories */}
          <div>
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              {t("dropdown.label")}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-foreground-800 bg-background-50 border border-background-200 rounded-lg hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="w-5 h-5 flex items-center justify-center text-primary-600 shrink-0">
                    <i className={cat.icon} />
                  </span>
                  <span className="truncate">{t(cat.key)}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Article links */}
          <div className="border-t border-background-200 pt-4">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              {t("nav.guides")}
            </p>
            <div className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {t(link.label)}
                </Link>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="border-t border-background-200 pt-4 flex gap-2">
            {[
              { code: "en", label: "EN" },
              { code: "pt-BR", label: "PT" },
              { code: "de", label: "DE" },
            ].map((l) => (
              <button
                key={l.code}
                onClick={() => switchLang(l.code)}
                className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
                  currentLang === l.code
                    ? "bg-primary-500 text-white"
                    : "bg-background-50 text-foreground-800 border border-background-200"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block text-center px-4 py-3 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            onClick={() => setMobileOpen(false)}
          >
            {t("nav.cta")}
          </a>
        </div>
      )}
    </header>
  );
}